import {Component, DestroyRef, inject, Input, OnDestroy, OnInit} from '@angular/core';
import {
  BokkzIbDataService,
  IncomeTaxEntity,
  PrestatiebeursTerugBetaald,
  PrestatiebeursTerugBetaaldOnderwijsJaar,
} from '@snelstart-ventures/fiscaal/domain-logic';
import {SelectFormControlItem, SharedUiFormControlsModule} from '@snelstart/ui-form-controls';
import {FormArray, FormControl, FormGroup} from '@angular/forms';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  Observable,
  shareReplay,
  Subscription,
  take,
  tap
} from "rxjs";
import {SharedUiButtonModule} from '@snelstart/ui-button';
import {SharedUiDetailsModule} from '@snelstart/ui-details';
import {CommonModule} from '@angular/common';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';

type PrestatiebeursTerugBetaaldFormGroup = FormGroup<{
  onderwijsJaar: FormControl<PrestatiebeursTerugBetaaldOnderwijsJaar | null>;
  terugbetaaldeBedrag: FormControl<number | null>;
  mbo: FormControl<boolean | null>;
  hboWo: FormControl<boolean | null>;
  periodeWaaroverTerugbetaaldMoetWordenInMaanden: FormGroup<{
    begindatum: FormControl<Date | null>;
    einddatum: FormControl<Date | null>;
  }>;
}>;

@Component({
  selector: 'snelstart-venture-fiscaal-feature-studiefinanciering-aangever',
  templateUrl: './studiefinanciering-aangever.component.html',
  styleUrls: ['./studiefinanciering-aangever.component.scss'],
  imports: [SharedUiFormControlsModule, SharedUiButtonModule, SharedUiDetailsModule, CommonModule],
})
export class StudiefinancieringAangeverComponent implements OnInit, OnDestroy {

  @Input() public id!: string;
  private dataService = inject(BokkzIbDataService);
  private destroyRef = inject(DestroyRef);
  private changeSubscriptions = new Subscription();

  public formGroup = new FormGroup({
    prestatiebeursTerugBetaald: new FormArray<PrestatiebeursTerugBetaaldFormGroup>([]),
  });

  public onderwijsJaar: SelectFormControlItem<PrestatiebeursTerugBetaaldOnderwijsJaar>[] = [
    {value: PrestatiebeursTerugBetaaldOnderwijsJaar.JAAR2014, label: 'Studiejaar 2014/2015'},
    {value: PrestatiebeursTerugBetaaldOnderwijsJaar.JAAR2013, label: 'Studiejaar 2013/2014'},
  ];

  public onderwijsJaar2025: SelectFormControlItem<PrestatiebeursTerugBetaaldOnderwijsJaar>[] = [
    {value: PrestatiebeursTerugBetaaldOnderwijsJaar.JAAR2014, label: 'Studiejaar 2014/2015'},
  ];

  public optionsJaNee: SelectFormControlItem<boolean>[] = [
    {value: true, label: 'Ja'},
    {value: false, label: 'Nee'},
  ];

  readonly incomeTaxEntity$: Observable<IncomeTaxEntity> = this.dataService.incomeTaxEntity$.pipe(
    filter((data): data is IncomeTaxEntity => !!data?.sstRelationId),
    takeUntilDestroyed(this.destroyRef),
    shareReplay({bufferSize: 1, refCount: true}),
  );

  isHboDisabled(i: number): boolean {
    const mboControl = this.prestatiebeursTerugBetaald.controls[i].get('mbo');
    return mboControl?.value === true;
  }

  isMboDisabled(i: number): boolean {
    const hboControl = this.prestatiebeursTerugBetaald.controls[i].get('hboWo');
    return hboControl?.value === true;
  }

  isHboAndMboDisabled(i: number): boolean {
    const mboControl = this.prestatiebeursTerugBetaald.controls[i].get('mbo');
    const hboControl = this.prestatiebeursTerugBetaald.controls[i].get('hboWo');
    return mboControl?.value === false && hboControl?.value === false;
  }

  public ngOnInit(): void {
    this.incomeTaxEntity$.pipe(
      take(1),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((data) => {
      this.updateForm(data.aangifte?.persoonsgebondenAftrek?.pgaScholingsuitgaven?.terugBetaaldePrestatiebeursAangever || []);
    });
    this.subscribeToChanges();
  }

  ngOnDestroy(): void {
    this.changeSubscriptions.unsubscribe();
    this.dataService.calculateLatestIncomeTaxReturn();
  }

  private subscribeToChanges(): void {
    this.changeSubscriptions.unsubscribe();
    this.changeSubscriptions = new Subscription();

    const extractRelevant = () =>
      this.prestatiebeursTerugBetaald.getRawValue().map(
        (w): PrestatiebeursTerugBetaald => ({
          mbo: w.mbo,
          hboWo: w.hboWo,
          onderwijsJaar: w.onderwijsJaar,
          terugbetaaldeBedrag: w.terugbetaaldeBedrag,
          periodeWaaroverTerugbetaaldMoetWordenInMaanden: w.periodeWaaroverTerugbetaaldMoetWordenInMaanden,
        }),
      );

    this.changeSubscriptions.add(
      this.prestatiebeursTerugBetaald.valueChanges
        .pipe(
          debounceTime(500),
          tap(() => this.saveForm()),
          map(() => extractRelevant()),
          distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)),
          takeUntilDestroyed(this.destroyRef),
        ).subscribe(),
    );
  }

  get prestatiebeursTerugBetaald(): FormArray {
    return this.formGroup.controls.prestatiebeursTerugBetaald as FormArray;
  }

  addRij(): void {
    this.prestatiebeursTerugBetaald.push(this.createFormGroup(), {emitEvent: false});
  }

  verwijderRij(index: number): void {
    this.prestatiebeursTerugBetaald.removeAt(index, {emitEvent: false});
    this.formGroup.updateValueAndValidity({emitEvent: false});
    this.formGroup.markAsDirty({emitEvent: false});
    this.saveForm();
  }

  createFormGroup(beurs?: PrestatiebeursTerugBetaald): FormGroup {
    return new FormGroup({
      onderwijsJaar: new FormControl<PrestatiebeursTerugBetaaldOnderwijsJaar | null>(beurs?.onderwijsJaar ?? null),
      terugbetaaldeBedrag: new FormControl<number | null>(beurs?.terugbetaaldeBedrag ?? null),
      mbo: new FormControl<boolean | null>(beurs?.mbo ?? null),
      hboWo: new FormControl<boolean | null>(beurs?.hboWo ?? null),
      periodeWaaroverTerugbetaaldMoetWordenInMaanden: new FormGroup({
        begindatum: new FormControl<Date | null>(beurs?.periodeWaaroverTerugbetaaldMoetWordenInMaanden?.begindatum ?? null),
        einddatum: new FormControl<Date | null>(beurs?.periodeWaaroverTerugbetaaldMoetWordenInMaanden?.einddatum ?? null),
      }),
    });
  }

  private updateForm(data: PrestatiebeursTerugBetaald[]): void {
    const lijst = data || [];
    const formArray = this.prestatiebeursTerugBetaald;

    while (formArray.length > lijst.length) {
      formArray.removeAt(formArray.length - 1, {emitEvent: false});
    }

    for (let i = 0; i < lijst.length; i++) {
      if (i < formArray.length) {
        const control = formArray.at(i) as FormGroup;
        control.patchValue(lijst[i], {emitEvent: false});
      } else {
        const formGroup = this.createFormGroup(lijst[i]);
        formArray.push(formGroup, {emitEvent: false});
      }
    }
  }

  saveForm(): void {
    const prestatiebeursTerugBetaald = this.formGroup
      .getRawValue()
      .prestatiebeursTerugBetaald.filter((prestatie) =>
        prestatie.mbo ||
        prestatie.hboWo ||
        prestatie.onderwijsJaar ||
        prestatie.periodeWaaroverTerugbetaaldMoetWordenInMaanden ||
        prestatie.terugbetaaldeBedrag,
      )
      .map((prestatie) => ({
          mbo: prestatie.mbo,
          hboWo: prestatie.hboWo,
          onderwijsJaar: prestatie.onderwijsJaar,
          periodeWaaroverTerugbetaaldMoetWordenInMaanden: prestatie.periodeWaaroverTerugbetaaldMoetWordenInMaanden,
          terugbetaaldeBedrag: prestatie.terugbetaaldeBedrag ?? 0,
        }) as PrestatiebeursTerugBetaald,
      );

    this.dataService.patchIncomeTaxEntity((currentEntity) => {
      if (!currentEntity.aangifte) {
        return currentEntity;
      }
      return {
        ...currentEntity,
        aangifte: {
          ...currentEntity.aangifte,
          persoonsgebondenAftrek: {
            ...currentEntity.aangifte?.persoonsgebondenAftrek,
            pgaScholingsuitgaven: {
              ...currentEntity.aangifte?.persoonsgebondenAftrek?.pgaScholingsuitgaven,
              terugBetaaldePrestatiebeursAangever: prestatiebeursTerugBetaald,
            }
          },
        },
      } as IncomeTaxEntity;
    });
  }
}

